import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  Edit2,
  Reply,
  SquareSlash,
  SquareCheckBig,
  ArrowRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  templateSections,
  getSectionOptionalFields,
} from '@/config/templateConfig';
import { ADVANCED_CREATORS } from '@/constants';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { useTemplateById, useTemplates } from '@/hooks/useTemplatesAPI';
import PageLayout from '@/layouts/PageLayout';

const TemplateDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { load, template, error } = useTemplateById();
  const { templates: existingTemplates } = useTemplates({
    type: template?.type,
  });

  const [config, setConfig] = useState({});
  const [templateName, setTemplateName] = useState('');
  const [nameError, setNameError] = useState('');
  const [values, setValues] = useState({});
  const navigate = useNavigate();
  const { isAdminOrSuperAdmin, groups } = useAdminAccess();
  const canUpdateTemplate = isAdminOrSuperAdmin || groups.includes('QA');
  const { showNotificationModal } = useNotificationModal();

  const handleFieldToggle = (fieldKey, checked) => {
    if (fieldKey === 'companyLogo' && !checked)
      setConfig((prev) => ({
        ...prev,
        ['companyName']: true,
      }));
    else if (fieldKey === 'companyName' && !checked)
      setConfig((prev) => ({
        ...prev,
        ['companyLogo']: true,
      }));

    setConfig((prev) => ({
      ...prev,
      [fieldKey]: checked,
    }));
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;

    setTemplateName(newName);

    // Clear previous errors
    setNameError('');

    if (!newName.trim()) {
      setNameError('Template name cannot be empty');

      return;
    }

    if (existingTemplates?.length > 0) {
      const isDuplicate = existingTemplates.some(
        (t) =>
          t.name.trim().toLowerCase() === newName.trim().toLowerCase() &&
          t.id !== selectedTemplate.id
      );

      if (isDuplicate) {
        setNameError(
          'A template with this name already exists for this document type'
        );
      }
    }
  };

  const canEditName = useMemo(
    () =>
      selectedTemplate && !ADVANCED_CREATORS.includes(selectedTemplate.creator),
    [selectedTemplate]
  );

  const isReadOnly = !canEditName || !canUpdateTemplate || !!selectedTemplate?.isUsed;

  const handleSelectAll = () => {
    const allOptionalFields = Object.keys(templateSections).reduce(
      (acc, sectionKey) => {
        const optionalFields = getSectionOptionalFields(sectionKey);
        optionalFields.forEach((field) => {
          acc[field] = true;
        });
        return acc;
      },
      {}
    );

    setConfig((prev) => ({
      ...prev,
      ...allOptionalFields,
    }));
  };

  const handleUnselectAll = () => {
    const allOptionalFields = Object.keys(templateSections).reduce(
      (acc, sectionKey) => {
        const optionalFields = getSectionOptionalFields(sectionKey);
        optionalFields.forEach((field) => {
          acc[field] = false;
        });
        return acc;
      },
      {}
    );

    setConfig((prev) => ({
      ...prev,
      ...allOptionalFields,
    }));
  };

  const renderFieldItem = (field, isMandatory = false) => {
    const isChecked = isMandatory || config[field.key] || false;

    return (
      <div
        key={field.key}
        className="flex items-center justify-between flex-wrap p-3 border rounded-md"
      >
        <div className="flex items-center space-x-3">
          <label
            className="text-sm font-medium cursor-pointer"
            htmlFor={field.key}
          >
            {field.label}
          </label>
          {field.tooltip && (
            <Tooltip>
              <TooltipTrigger>
                <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-xs">
                  ?
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Switch
          id={field.key}
          checked={isChecked}
          onCheckedChange={(checked) => handleFieldToggle(field.key, checked)}
          disabled={isMandatory || isReadOnly}
        />
        {['controlCopy', 'confidentiality'].includes(field.key) &&
          isChecked && (
            <div className="basis-full">
              <Textarea
                className="font-mono resize-none mt-2"
                disabled={isReadOnly}
                placeholder={`Enter content for ${field.label}...`}
                rows={4}
                value={
                  field.key === 'controlCopy'
                    ? values?.controlCopyStatement
                    : values?.confidentialityStatement
                }
                onChange={(e) => {
                  const val = e.target.value;

                  if (field.key === 'controlCopy') {
                    setValues({ ...values, controlCopyStatement: val });
                  } else if (field.key === 'confidentiality') {
                    setValues({ ...values, confidentialityStatement: val });
                  }
                }}
              />
            </div>
          )}
      </div>
    );
  };

  const renderSection = (sectionKey) => {
    const section = templateSections[sectionKey];

    if (!section) return null;

    return (
      <div>
        <h3 className="text-lg font-bold">{section.title}</h3>
        <div className="space-y-4">
          {section.items.filter(({ required }) => required).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">
                Mandatory Fields
              </h4>
              <div className="space-y-2">
                {section.items
                  .filter(({ required }) => required)
                  .map((field) => renderFieldItem(field, true))}
              </div>
            </div>
          )}

          {section.items.filter(({ required }) => !required).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">
                Optional Fields
              </h4>
              <div className="space-y-2">
                {section.items
                  .filter(({ required }) => !required)
                  .map((field) => renderFieldItem(field, false))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const isPreviewDisabled = useMemo(() => {
    return !!nameError || !templateName.trim();
  }, [nameError, templateName]);

  const handleBack = () => {
    navigate('/templates');
    setSelectedTemplate(null);
  };

  const handlePreviewTemplate = (updatedTemplate) => {
    if (!updatedTemplate.name || !updatedTemplate.name.trim()) {
      showNotificationModal(
        'Validation Error',
        'Template name is required for preview',
        'error'
      );

      return;
    }

    setSelectedTemplate(updatedTemplate);
    navigate(`/template/${selectedTemplate.id}/preview`, {
      state: { templateDraft: updatedTemplate },
    });
  };

  const handlePreview = () => {
    handlePreviewTemplate({
      ...selectedTemplate,
      name: templateName.trim(),
      config,
      values,
    });
  };

  useEffect(() => {
    if (id) load(id);
  }, [id, load]);

  useEffect(() => {
    const draft = location.state?.templateDraft;
    setSelectedTemplate(template && draft?.id === template.id
      ? { ...template, name: draft.name, config: draft.config, values: draft.values }
      : template);
  }, [template, location.state]);

  useEffect(() => {
    if (selectedTemplate) {
      setConfig(selectedTemplate.config || {});
      setTemplateName(selectedTemplate.name || '');
      setNameError('');
      setValues(selectedTemplate.values);
    }
  }, [selectedTemplate]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-destructive">
        Error loading template: {error}
      </div>
    );
  }

  if (!template?.id) return <>Loading...</>;

  if (!selectedTemplate)
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No template selected</p>
      </div>
    );

  return (
    <TooltipProvider>
      <PageLayout
        subtitle="Configure template fields and options"
        title="Template View"
        toolbarHead={
          !isReadOnly && (
            <>
              <Button onClick={handleBack} variant="outline">
                <Reply className="w-4 h-4 mr-2" />
                BACK
              </Button>
              <Button onClick={handleSelectAll} variant="outline">
                <SquareCheckBig className="w-4 h-4 mr-2" />
                SELECT ALL
              </Button>
              <Button onClick={handleUnselectAll} variant="outline">
                <SquareSlash className="w-4 h-4 mr-2" />
                UNSELECT ALL
              </Button>
            </>
          )
        }
        toolbarTail={
          <>
            {!isReadOnly && (
              <Button
                disabled={isPreviewDisabled}
                onClick={handlePreview}
                variant="outline"
              >
                PREVIEW
                <ArrowRight className="w-4 h-4 mr-2" />
              </Button>
            )}
          </>
        }
      >
        {/* Header */}
        <div className="mb-3  flex flex-row content-end">
          <>
            {canEditName ? (
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Input
                    disabled={isReadOnly}
                    value={templateName}
                    onChange={handleNameChange}
                    placeholder="Enter template name"
                    className={`text-lg font-semibold ${nameError ? 'border-destructive' : ''}`}
                  />
                  <Edit2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                {nameError && (
                  <p className="text-sm text-destructive mt-1">{nameError}</p>
                )}
              </div>
            ) : (
              <span className="text-2xl sm:text-3xl font-bold tracking-tight">
                {selectedTemplate.name}
              </span>
            )}
          </>
          {/* Configure template fields and options */}
          {!canEditName && (
            <div className="ml-2 content-center">
              <span className="text-xs bg-muted px-2 py-1 rounded">
                System Template - Name cannot be changed
              </span>
            </div>
          )}
        </div>

        {/* Template Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Template Configuration</CardTitle>
            <CardDescription>
              Select which fields should be included in this template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderSection('information')}
            {renderSection('history')}
            {renderSection('content')}
          </CardContent>
        </Card>
      </PageLayout>
    </TooltipProvider>
  );
};

export default TemplateDetailPage;
